import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/Logo";
import {
  Package, Plane, ArrowRight, ShieldCheck, MapPin, MessageSquare, Search, Route, Menu,
} from "lucide-react";

const HERO_IMG = "/banner.png";

const HOW = [
  { icon: Package, title: "Post a package", text: "Senders describe what they need delivered and where it needs to go." },
  { icon: Search, title: "Match a traveller", text: "Find travellers already heading your way with spare capacity." },
  { icon: MessageSquare, title: "Connect & chat", text: "Agree the details securely in-app once a connection is accepted." },
  { icon: Route, title: "Track to delivery", text: "Follow every step from request to hand-over with a live status timeline." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const go = (intent) => {
    if (user) navigate(intent === "send" ? "/create-delivery" : "/create-trip");
    else navigate("/signup");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <BrandLogo />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Button data-testid="landing-dashboard-btn" onClick={() => navigate("/dashboard")} className="rounded-full">
                Open App <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <>
                <Button variant="ghost" data-testid="landing-login-btn" onClick={() => navigate("/login")} className="rounded-full">Log in</Button>
                <Button data-testid="landing-signup-btn" onClick={() => navigate("/signup")} className="rounded-full">Create account</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent text-primary px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <Plane className="w-3.5 h-3.5" /> Peer-to-peer delivery
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
            Travel.<br /><span className="text-primary">Connect.</span> Deliver.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-lg leading-relaxed">
            Connect travellers with people who need to send packages. Pack Mate turns spare luggage space into a smarter, friendlier way to move things.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button size="lg" data-testid="hero-send-package-btn" onClick={() => go("send")} className="rounded-full h-12 px-7 text-base">
              <Package className="w-5 h-5" /> Send a Package
            </Button>
            <Button size="lg" variant="outline" data-testid="hero-travel-deliver-btn" onClick={() => go("travel")} className="rounded-full h-12 px-7 text-base">
              <Plane className="w-5 h-5" /> Travel &amp; Deliver
            </Button>
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Verified profiles</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> Real maps</span>
          </div>
        </div>
        <div className="relative animate-fade-up lg:col-span-2 max-w-3xl mx-auto w-full">
          <div className="absolute -inset-4 bg-primary/10 rounded-[2rem] blur-2xl" />
          <img src={HERO_IMG} alt="Pack Mate — connect travellers and senders to deliver packages" className="relative rounded-[2rem] shadow-2xl w-full h-auto" />
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">How Pack Mate works</h2>
          <p className="mt-3 text-slate-600">A modern marketplace connecting senders and travellers along the same route.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {HOW.map((h, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center">
                <h.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">{h.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{h.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-[2rem] bg-primary px-6 sm:px-12 py-14 text-center text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
          <h2 className="relative text-2xl sm:text-4xl font-bold">Ready to move something?</h2>
          <p className="relative mt-3 text-white/85 max-w-xl mx-auto">Create an account and start sending or carrying packages in minutes.</p>
          <Button size="lg" data-testid="cta-get-started-btn" onClick={() => navigate(user ? "/dashboard" : "/signup")} className="relative mt-7 rounded-full h-12 px-8 bg-white text-primary hover:bg-white/90 text-base">
            {user ? "Open the app" : "Get started free"} <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-600">
            <BrandLogo textClassName="font-semibold" />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-primary">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-primary">Terms &amp; Conditions</Link>
            <a href="https://sites.google.com/view/pack-mate/delete-account" target="_blank" rel="noreferrer" data-testid="footer-delete-account" className="hover:text-primary">Account Deletion</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
